import React, { useMemo, useState } from 'react';
import {
  Modal,
  Upload,
  Button,
  Steps,
  Typography,
  Table,
  Alert,
  Space,
  Progress,
  Result,
  Card,
  Row,
  Col,
  Tag,
  message,
  Tooltip,
  Checkbox,
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import type { UploadProps, UploadFile } from 'antd';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

interface ValidationError {
  row: number;
  field: string;
  value: any;
  error: string;
  severity: 'error' | 'warning';
}

interface PreviewItem {
  row: number;
  merchantReference: string;
  sku: string;
  productName: string;
  barcode: string;
  brand?: string;
  length?: number;
  width?: number;
  height?: number;
  weight?: number;
  packagingLength?: number;
  packagingWidth?: number;
  packagingHeight?: number;
  packagingWeight?: number;
  temperatureRequirement?: string;
  baseUOM: string;
  currentStock: number;
  reorderLevel: number;
  safetyStock: number;
  maxStock: number;
  status: 'valid' | 'error' | 'warning';
  errors: ValidationError[];
}

interface ProductBulkUploadModalProps {
  visible: boolean;
  onCancel: () => void;
  onUploadComplete: (data: any[]) => void;
}

const ProductBulkUploadModal: React.FC<ProductBulkUploadModalProps> = ({
  visible,
  onCancel,
  onUploadComplete,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<UploadFile | null>(null);
  const [previewData, setPreviewData] = useState<PreviewItem[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // 字段說明
  const fieldDescriptions = [
    { field: 'merchantReference', name: '商家參考', required: true, description: '商家的產品參考編號' },
    { field: 'sku', name: 'SKU ID', required: true, description: '產品的SKU識別碼' },
    { field: 'barcode', name: '條形碼', required: true, description: '產品條形碼' },
    { field: 'productName', name: '產品名稱', required: true, description: '產品名稱' },
    { field: 'productNameEnglish', name: '英文產品名稱', required: false, description: '產品英文名稱' },
    { field: 'productNameChinese', name: '中文產品名稱', required: false, description: '產品中文名稱' },
    { field: 'description', name: '描述', required: false, description: '產品描述' },
    { field: 'brand', name: '品牌', required: false, description: '產品品牌' },
    { field: 'length', name: '產品長度(cm)', required: false, description: '產品本身長度' },
    { field: 'width', name: '產品寬度(cm)', required: false, description: '產品本身寬度' },
    { field: 'height', name: '產品高度(cm)', required: false, description: '產品本身高度' },
    { field: 'weight', name: '產品重量(kg)', required: false, description: '產品本身重量' },
    { field: 'packagingLength', name: '包裝長度(cm)', required: false, description: '包裝長度，用於CBM計算' },
    { field: 'packagingWidth', name: '包裝寬度(cm)', required: false, description: '包裝寬度，用於CBM計算' },
    { field: 'packagingHeight', name: '包裝高度(cm)', required: false, description: '包裝高度，用於CBM計算' },
    { field: 'packagingWeight', name: '包裝重量(kg)', required: false, description: '包裝重量' },
    { field: 'temperatureRequirement', name: '溫度要求', required: false, description: '存儲溫度要求' },
    { field: 'baseUOM', name: '基本單位', required: true, description: '產品基本計量單位' },
    { field: 'packagingUOM', name: '包裝單位', required: false, description: '包裝計量單位' },
    { field: 'conversionFactor', name: '轉換因子', required: true, description: '包裝單位與基本單位的轉換比例' },
    { field: 'currentStock', name: '當前庫存', required: true, description: '當前庫存數量' },
    { field: 'reorderLevel', name: '重新訂購水平', required: true, description: '重新訂購閾值' },
    { field: 'safetyStock', name: '安全庫存', required: true, description: '安全庫存數量' },
    { field: 'maxStock', name: '最大庫存', required: true, description: '最大庫存數量' },
  ];

  // 可選字段勾選狀態（依賴 fieldDescriptions，需在其定義之後）
  const optionalDefaultKeys = useMemo(() => fieldDescriptions.filter(f => !f.required).map(f => f.field), [fieldDescriptions]);
  const [selectedOptionalKeys, setSelectedOptionalKeys] = useState<string[]>(optionalDefaultKeys);

  // 下載模板（根據勾選生成，必填在前，可選在後）
  const downloadTemplate = () => {
    const required = fieldDescriptions.filter(f => f.required);
    const optional = fieldDescriptions.filter(f => !f.required && selectedOptionalKeys.includes(f.field));
    const selected = [...required, ...optional];

    const headers = selected.map(field => field.name).join(',');
    const example1: Record<string, string> = {
      merchantReference: 'MERCH-001', sku: 'SKU-001', barcode: '1234567890123', productName: '智能筆記本',
      productNameEnglish: 'Smart Notebook', productNameChinese: '智能筆記本', description: '高性能智能筆記本，配備先進處理器', brand: 'TechBrand',
      length: '30', width: '20', height: '2', weight: '1.5', packagingLength: '35', packagingWidth: '25', packagingHeight: '5', packagingWeight: '2.0',
      temperatureRequirement: 'room_temperature', baseUOM: 'PCS', packagingUOM: 'BOX', conversionFactor: '1', currentStock: '100', reorderLevel: '20', safetyStock: '10', maxStock: '500'
    };
    const example2: Record<string, string> = {
      merchantReference: 'MERCH-002', sku: 'SKU-002', barcode: '9876543210987', productName: '無線耳機',
      productNameEnglish: 'Wireless Earphones', productNameChinese: '無線耳機', description: '高音質無線藍牙耳機', brand: 'AudioPro',
      length: '15', width: '10', height: '3', weight: '0.2', packagingLength: '20', packagingWidth: '15', packagingHeight: '8', packagingWeight: '0.5',
      temperatureRequirement: 'dry', baseUOM: 'PAIR', packagingUOM: 'BOX', conversionFactor: '2', currentStock: '50', reorderLevel: '10', safetyStock: '5', maxStock: '200'
    };
    const sampleRow1 = selected.map(f => example1[f.field] ?? '').join(',');
    const sampleRow2 = selected.map(f => example2[f.field] ?? '').join(',');
    const csvContent = `${headers}\n${sampleRow1}\n${sampleRow2}`;
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', '產品批量上傳模板.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.success('模板下載成功');
  };

  // 文件上傳配置
  const uploadProps: UploadProps = {
    name: 'file',
    accept: '.csv,.xlsx,.xls',
    showUploadList: false,
    beforeUpload: (file) => {
      setUploadedFile(file);
      processFile(file);
      return false;
    },
  };

  // 處理文件
  const processFile = async (file: UploadFile) => {
    setIsProcessing(true);
    setUploadProgress(0);

    try {
      const fileContent = await readFileContent(file);
      setUploadProgress(30);

      const parsedData = parseCSVContent(fileContent);
      setUploadProgress(60);

      const validatedData = validateParsedData(parsedData);
      setUploadProgress(90);

      const errors = generateValidationErrors(validatedData);
      
      setPreviewData(validatedData);
      setValidationErrors(errors);
      setUploadProgress(100);
      
      setIsProcessing(false);
      setCurrentStep(1);
    } catch (error) {
      console.error('文件處理錯誤:', error);
      message.error('文件處理失敗，請檢查文件格式');
      setIsProcessing(false);
      setUploadProgress(0);
    }
  };

  // 讀取文件內容
  const readFileContent = (file: UploadFile): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        resolve(content);
      };
      reader.onerror = () => reject(new Error('文件讀取失敗'));
      reader.readAsText(file as File, 'UTF-8');
    });
  };

  // 解析CSV內容
  const parseCSVContent = (content: string): any[] => {
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV文件格式不正確，至少需要標題行和一行數據');
    }

    // 使用與行相同的解析邏輯處理表頭，避免帶引號或逗號的表頭被錯切
    const headers = parseCSVLine(lines[0]).map(header => header.trim().replace(/"/g, ''));
    const data: any[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length === headers.length) {
        const row: any = { row: i };
        headers.forEach((header, index) => {
          row[getFieldKey(header)] = values[index]?.trim() || '';
        });
        data.push(row);
      }
    }

    return data;
  };

  // 解析CSV行
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current);
    return result.map(val => val.replace(/"/g, ''));
  };

  // 字段映射
  const getFieldKey = (header: string): string => {
    const normalized = header.trim();
    // 兼容常見別名與大小寫/空格差異
    const fieldMap: Record<string, string> = {
      '商家參考': 'merchantReference',
      '商家参考': 'merchantReference',
      'SKU ID': 'sku',
      '條形碼': 'barcode',
      '条形码': 'barcode',
      'BARCODE': 'barcode',
      '產品名稱': 'productName',
      '产品名称': 'productName',
      'PRODUCT NAME': 'productName',
      '英文產品名稱': 'productNameEnglish',
      '中文產品名稱': 'productNameChinese',
      '描述': 'description',
      '品牌': 'brand',
      '產品長度(cm)': 'length',
      '產品寬度(cm)': 'width',
      '產品高度(cm)': 'height',
      '產品重量(kg)': 'weight',
      '包裝長度(cm)': 'packagingLength',
      '包裝寬度(cm)': 'packagingWidth',
      '包裝高度(cm)': 'packagingHeight',
      '包裝重量(kg)': 'packagingWeight',
      '溫度要求': 'temperatureRequirement',
      '基本單位': 'baseUOM',
      '包裝單位': 'packagingUOM',
      '轉換因子': 'conversionFactor',
      '當前庫存': 'currentStock',
      '重新訂購水平': 'reorderLevel',
      '安全庫存': 'safetyStock',
      '最大庫存': 'maxStock',
    };
    // 先嘗試精確匹配，再嘗試小寫匹配
    if (fieldMap[normalized]) return fieldMap[normalized];
    const lower = normalized.toLowerCase();
    const lowerMap: Record<string, string> = {
      'merchant reference': 'merchantReference',
      'sku': 'sku',
      'sku id': 'sku',
      'barcode': 'barcode',
      'product name': 'productName',
      'base uom': 'baseUOM',
      'packaging uom': 'packagingUOM',
      'conversion factor': 'conversionFactor',
      'current stock': 'currentStock',
      'reorder level': 'reorderLevel',
      'safety stock': 'safetyStock',
      'max stock': 'maxStock',
    };
    return lowerMap[lower] || header;
  };

  // 驗證數據
  const validateParsedData = (data: any[]): PreviewItem[] => {
    return data.map((row, index) => {
      const errors: ValidationError[] = [];
      let status: 'valid' | 'warning' | 'error' = 'valid';

      // 必填字段驗證
      const requiredFields = [
        { key: 'merchantReference', name: '商家參考' },
        { key: 'sku', name: 'SKU' },
        { key: 'barcode', name: '條形碼' },
        { key: 'productName', name: '產品名稱' },
        { key: 'baseUOM', name: '基本單位' },
        { key: 'conversionFactor', name: '轉換因子' },
        { key: 'currentStock', name: '當前庫存' },
        { key: 'reorderLevel', name: '重新訂購水平' },
        { key: 'safetyStock', name: '安全庫存' },
        { key: 'maxStock', name: '最大庫存' }
      ];

      requiredFields.forEach(field => {
        if (!row[field.key] || row[field.key].toString().trim() === '') {
          errors.push({
            row: index + 1,
            field: field.key,
            value: row[field.key],
            error: `${field.name}不能為空`,
            severity: 'error'
          });
          status = 'error';
        }
      });

      // 數字字段驗證
      const numberFields = ['length', 'width', 'height', 'weight', 'packagingLength', 'packagingWidth', 'packagingHeight', 'packagingWeight', 'conversionFactor', 'currentStock', 'reorderLevel', 'safetyStock', 'maxStock'];
      numberFields.forEach(field => {
        if (row[field] && isNaN(Number(row[field]))) {
          errors.push({
            row: index + 1,
            field: field,
            value: row[field],
            error: `${field}必須為數字`,
            severity: 'error'
          });
          status = 'error';
        }
      });

      return {
        row: index + 1,
        merchantReference: row.merchantReference || '',
        sku: row.sku || '',
        productName: row.productName || '',
        barcode: row.barcode || '',
        brand: row.brand || '',
        length: Number(row.length) || undefined,
        width: Number(row.width) || undefined,
        height: Number(row.height) || undefined,
        weight: Number(row.weight) || undefined,
        packagingLength: Number(row.packagingLength) || undefined,
        packagingWidth: Number(row.packagingWidth) || undefined,
        packagingHeight: Number(row.packagingHeight) || undefined,
        packagingWeight: Number(row.packagingWeight) || undefined,
        temperatureRequirement: row.temperatureRequirement || undefined,
        baseUOM: row.baseUOM || '',
        currentStock: Number(row.currentStock) || 0,
        reorderLevel: Number(row.reorderLevel) || 0,
        safetyStock: Number(row.safetyStock) || 0,
        maxStock: Number(row.maxStock) || 0,
        status,
        errors
      };
    });
  };

  // 生成驗證錯誤列表
  const generateValidationErrors = (data: PreviewItem[]): ValidationError[] => {
    const errors: ValidationError[] = [];
    data.forEach(item => {
      errors.push(...item.errors);
    });
    return errors;
  };

  // 確認導入
  const confirmImport = () => {
    const validData = previewData.filter(item => item.status === 'valid' || item.status === 'warning');
    
    if (validData.length === 0) {
      message.error('沒有有效的數據可以導入');
      return;
    }

    onUploadComplete(validData);
    setCurrentStep(2);
    
    setTimeout(() => {
      handleClose();
      message.success(`成功導入 ${validData.length} 個產品`);
    }, 2000);
  };

  // 關閉彈窗
  const handleClose = () => {
    setCurrentStep(0);
    setUploadedFile(null);
    setPreviewData([]);
    setValidationErrors([]);
    setIsProcessing(false);
    setUploadProgress(0);
    onCancel();
  };

  // 預覽表格列定義
  const previewColumns: ColumnsType<PreviewItem> = [
    {
      title: '行號',
      dataIndex: 'row',
      width: 60,
      render: (row, record) => (
        <Space>
          <span>{row}</span>
          {record.status === 'error' && <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
          {record.status === 'warning' && <ExclamationCircleOutlined style={{ color: '#faad14' }} />}
          {record.status === 'valid' && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
        </Space>
      ),
    },
    {
      title: '產品資訊',
      key: 'product',
      width: 200,
      render: (_, record) => (
        <div>
          <div><strong>{record.productName || <Text type="danger">缺少產品名稱</Text>}</strong></div>
          <Text type="secondary" style={{ fontSize: '12px' }}>SKU: {record.sku}</Text><br />
          <Text type="secondary" style={{ fontSize: '12px' }}>商家參考: {record.merchantReference}</Text>
        </div>
      ),
    },
    {
      title: '條形碼',
      dataIndex: 'barcode',
      width: 120,
    },
    {
      title: '尺寸',
      key: 'dimensions',
      width: 150,
      render: (_, record) => (
        <div>
          {record.packagingLength && record.packagingWidth && record.packagingHeight ? (
            <>
              <div style={{ fontSize: '12px' }}>包裝: {record.packagingLength}×{record.packagingWidth}×{record.packagingHeight}cm</div>
              <div style={{ fontSize: '12px', color: '#666' }}>產品: {record.length}×{record.width}×{record.height}cm</div>
            </>
          ) : (
            <Text type="secondary">未提供尺寸</Text>
          )}
        </div>
      ),
    },
    {
      title: '庫存',
      key: 'stock',
      width: 120,
      render: (_, record) => (
        <div>
          <div>當前: {record.currentStock}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            安全: {record.safetyStock} | 最大: {record.maxStock}
          </div>
        </div>
      ),
    },
    {
      title: '狀態',
      key: 'status',
      width: 100,
      render: (_, record) => {
        let color = 'default';
        let text = '未知';
        
        switch (record.status) {
          case 'valid':
            color = 'success';
            text = '有效';
            break;
          case 'warning':
            color = 'warning';
            text = '警告';
            break;
          case 'error':
            color = 'error';
            text = '錯誤';
            break;
        }
        
        return <Tag color={color}>{text}</Tag>;
      },
    },
  ];

  const errorColumns: ColumnsType<ValidationError> = [
    {
      title: '行號',
      dataIndex: 'row',
      width: 60,
    },
    {
      title: '字段',
      dataIndex: 'field',
      width: 120,
    },
    {
      title: '值',
      dataIndex: 'value',
      width: 150,
      render: (value) => value || <Text type="secondary">空值</Text>,
    },
    {
      title: '錯誤信息',
      dataIndex: 'error',
      render: (error, record) => (
        <Text type={record.severity === 'error' ? 'danger' : 'warning'}>
          {error}
        </Text>
      ),
    },
    {
      title: '嚴重性',
      dataIndex: 'severity',
      width: 80,
      render: (severity) => (
        <Tag color={severity === 'error' ? 'red' : 'orange'}>
          {severity === 'error' ? '錯誤' : '警告'}
        </Tag>
      ),
    },
  ];

  const steps = [
    {
      title: '上傳文件',
      description: '選擇並上傳CSV/Excel文件',
    },
    {
      title: '數據預覽',
      description: '查看和驗證數據',
    },
    {
      title: '導入完成',
      description: '數據導入成功',
    },
  ];

  return (
    <Modal
      title="批量上傳產品"
      open={visible}
      onCancel={handleClose}
      width={1200}
      footer={null}
      destroyOnClose
    >
      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        {steps.map(step => (
          <Step key={step.title} title={step.title} description={step.description} />
        ))}
      </Steps>

      {currentStep === 0 && (
        <div>
          <Alert
            message="批量上傳說明"
            description="現在支持直接上傳CSV文件，系統將自動解析和驗證數據。您也可以下載模板文件作為參考。"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Card title="步驟 1: 下載模板" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Paragraph>
                請先下載產品批量上傳模板文件，模板包含所有必需的字段和格式說明。支持CSV格式，請確保文件編碼為UTF-8。
              </Paragraph>
              <div style={{ 
                backgroundColor: '#f6ffed', 
                border: '1px solid #b7eb8f', 
                borderRadius: '6px', 
                padding: '12px', 
                marginBottom: '16px' 
              }}>
                <Text strong style={{ color: '#389e0d' }}>✓ 提示</Text>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#666' }}>
                  <li>必填字段不能為空，包括商家參考、SKU、條形碼、產品名稱等</li>
                  <li>數量字段請輸入有效的數字</li>
                  <li>CSV文件請保存為UTF-8編碼，避免中文亂碼</li>
                  <li>包裝尺寸用於CBM計算，產品尺寸記錄產品本身資訊</li>
                </ul>
              </div>
              <Button 
                type="primary" 
                icon={<DownloadOutlined />} 
                onClick={downloadTemplate}
                size="large"
              >
                下載CSV模板
              </Button>
            </Space>
          </Card>

          <Card title="步驟 2: 上傳文件">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Paragraph>
                填寫完成後，請上傳文件進行數據驗證和預覽。
              </Paragraph>
              <Upload {...uploadProps}>
                <Button 
                  icon={<UploadOutlined />} 
                  size="large"
                  loading={isProcessing}
                >
                  {isProcessing ? '處理中...' : '選擇文件上傳'}
                </Button>
              </Upload>
              {uploadedFile && (
                <div>
                  <Text>已選擇文件: {uploadedFile.name}</Text>
                  {isProcessing && (
                    <Progress percent={uploadProgress} style={{ marginTop: 8 }} />
                  )}
                </div>
              )}
            </Space>
          </Card>

          <Card title="字段說明（必填在前、可選可勾選，用於生成模板）" style={{ marginTop: 16 }}>
            <Title level={5}>必填字段</Title>
            <Row gutter={16} style={{ marginBottom: 12 }}>
              {fieldDescriptions.filter(f => f.required).map((field) => (
                <Col span={8} key={field.field} style={{ marginBottom: 8 }}>
                  <Space>
                    <Tag color="red">必填</Tag>
                    <Text strong>{field.name}</Text>
                  </Space>
                  <div style={{ fontSize: '12px', color: '#666', marginLeft: 16 }}>
                    {field.description}
                  </div>
                </Col>
              ))}
            </Row>

            <Title level={5}>可選字段（勾選後包含在模板中）</Title>
            <Row gutter={16}>
              {fieldDescriptions.filter(f => !f.required).map((field) => (
                <Col span={8} key={field.field} style={{ marginBottom: 8 }}>
                  <Space align="start">
                    <Checkbox
                      checked={selectedOptionalKeys.includes(field.field)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? Array.from(new Set([...selectedOptionalKeys, field.field]))
                          : selectedOptionalKeys.filter(k => k !== field.field);
                        setSelectedOptionalKeys(next);
                      }}
                    />
                    <div>
                      <Space>
                        <Tag>可選</Tag>
                        <Text strong>{field.name}</Text>
                      </Space>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {field.description}
                      </div>
                    </div>
                  </Space>
                </Col>
              ))}
            </Row>
          </Card>
        </div>
      )}

      {currentStep === 1 && (
        <div>
          <Alert
            message={`數據驗證完成，共 ${previewData.length} 行數據`}
            description={
              <Space>
                <span>有效: {previewData.filter(item => item.status === 'valid').length}</span>
                <span>警告: {previewData.filter(item => item.status === 'warning').length}</span>
                <span>錯誤: {previewData.filter(item => item.status === 'error').length}</span>
              </Space>
            }
            type={validationErrors.filter(e => e.severity === 'error').length > 0 ? 'warning' : 'success'}
            showIcon
            style={{ marginBottom: 16 }}
          />

          {validationErrors.length > 0 && (
            <Card 
              title={
                <Space>
                  <span>驗證錯誤</span>
                  <Tag color="red">
                    {validationErrors.filter(e => e.severity === 'error').length} 個錯誤
                  </Tag>
                  <Tag color="orange">
                    {validationErrors.filter(e => e.severity === 'warning').length} 個警告
                  </Tag>
                </Space>
              } 
              style={{ marginBottom: 16 }}
            >
              {validationErrors.filter(e => e.severity === 'error').length > 0 && (
                <Alert
                  message="存在錯誤項目"
                  description="請修正所有錯誤項目後才能進行導入。警告項目不會阻止導入，但建議檢查。"
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
              <Table
                dataSource={validationErrors}
                columns={errorColumns}
                rowKey={(record, index) => `${record.row}-${record.field}-${index}`}
                size="small"
                pagination={{ pageSize: 5 }}
              />
            </Card>
          )}

          <Card title="數據預覽" extra={
            <Space>
              <Button onClick={() => setCurrentStep(0)}>重新上傳</Button>
              {(() => {
                const hasErrors = validationErrors.filter(e => e.severity === 'error').length > 0;
                const validDataCount = previewData.filter(item => item.status === 'valid' || item.status === 'warning').length;
                const isDisabled = validDataCount === 0 || hasErrors;

                return (
                  <Tooltip 
                    title={
                      hasErrors
                        ? `存在 ${validationErrors.filter(e => e.severity === 'error').length} 個錯誤，請修正後重新上傳`
                        : validDataCount === 0
                        ? '沒有有效的數據可以導入'
                        : '確認導入數據'
                    }
                  >
                    <Button 
                      type={isDisabled ? 'default' : 'primary'}
                      onClick={isDisabled ? undefined : confirmImport}
                      disabled={isDisabled}
                      style={{
                        backgroundColor: isDisabled ? '#f5f5f5' : undefined,
                        borderColor: isDisabled ? '#d9d9d9' : undefined,
                        color: isDisabled ? 'rgba(0, 0, 0, 0.25)' : undefined,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.6 : 1
                      }}
                    >
                      確認導入
                    </Button>
                  </Tooltip>
                );
              })()}
            </Space>
          }>
            <Table
              dataSource={previewData}
              columns={previewColumns}
              rowKey="row"
              size="small"
              pagination={{ pageSize: 10 }}
              rowClassName={(record) => {
                switch (record.status) {
                  case 'error':
                    return 'row-error';
                  case 'warning':
                    return 'row-warning';
                  case 'valid':
                    return 'row-valid';
                  default:
                    return '';
                }
              }}
            />
          </Card>
        </div>
      )}

      {currentStep === 2 && (
        <Result
          status="success"
          title="批量上傳完成"
          subTitle={`成功導入 ${previewData.filter(item => item.status === 'valid' || item.status === 'warning').length} 個產品`}
          icon={<CheckCircleOutlined />}
        />
      )}

      <style>{`
        .row-error {
          background-color: #fff2f0 !important;
        }
        .row-warning {
          background-color: #fffbe6 !important;
        }
        .row-valid {
          background-color: #f6ffed !important;
        }
        
        /* 強制禁用按鈕樣式 */
        .ant-btn-primary[disabled], 
        .ant-btn-primary[disabled]:hover, 
        .ant-btn-primary[disabled]:focus, 
        .ant-btn-primary[disabled]:active {
          background-color: #d9d9d9 !important;
          border-color: #d9d9d9 !important;
          color: #666 !important;
          cursor: not-allowed !important;
          opacity: 0.6 !important;
        }
        
        /* 確保禁用狀態下的樣式覆蓋 */
        .ant-btn[disabled] {
          background-color: #f5f5f5 !important;
          border-color: #d9d9d9 !important;
          color: rgba(0, 0, 0, 0.25) !important;
          cursor: not-allowed !important;
          box-shadow: none !important;
        }
      `}</style>
    </Modal>
  );
};

export default ProductBulkUploadModal;
